import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UserstoryService } from '../../../services/userstory.service';
import { SettingsService } from '../../../services/settings.service';
import { Userstory } from '../../../models/userstory';
import { forkJoin, Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { KanbanProjectService } from '../../../services/kanban-project.service';
import { BoardState } from '../../../models/boardstate';
import { CreateUserstoryDialogComponent } from '../../create-userstory-dialog/create-userstory-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Person } from '../../../models/person';
import { PersonService } from '../../../services/person.service';
import { Issue } from '../../../models/issue';
import { AwallComponentEvent } from '../../../models/awallComponentEvent';
import { CommunicationService } from '../../../services/communication.service';

@Component({
  selector: 'app-kanban-backlog',
  templateUrl: './backlog.component.html',
  styleUrls: ['./backlog.component.scss']
  })
export class KanbanBacklogComponent implements OnInit, OnDestroy {
  @Input() public tasks: Userstory[];
  public subscriptions: Subscription[] = [];
  public backlogColumn;
  private timer;
  public boardConfiguration: BoardState;
  @Input() public parent;
  private createUserStoryDialogRef: MatDialogRef<CreateUserstoryDialogComponent>;
  public team: Person[] = [];
  public summaryFilter: string;
  public selectedAssignee: string;
  public selectedReporter: string;
  public selectedIssuetype: string;
  public selectedCreationDate: string;
  private filterTerms = {};
  public static readonly componentId = 'KanbanBacklogComponent';

  constructor(
    private communicationService: CommunicationService,
    private settingsService: SettingsService,
    private userStoryService: UserstoryService,
    private projectService: KanbanProjectService,
    private addUserStoryDialog: MatDialog,
    private personService: PersonService,
  ) { }

  ngOnInit() {
    this.communicationService.registerComponent(KanbanBacklogComponent.componentId, this);
    this.subscriptions.push(this.settingsService.getUpdateObservable().subscribe(res => {
      if (this.settingsService.getProjectType() === 'kanban') {
        this.loadData();
        this.loadTeamData();
      }
    }));
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(KanbanBacklogComponent.componentId);
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) { return; }

    if (event.name === 'update-backlog') {
      this.loadData();
    }
  }

  private loadData(): void {
    const projectName = this.settingsService.getProjectName();
    forkJoin([
      this.userStoryService.getIssuesFromKanbanBacklog(projectName, this.filterTerms),
      this.projectService.getBoardStates(projectName)
    ]).subscribe(([userStoriesData, boardConfiguration]) => {
      this.tasks = userStoriesData;
      this.boardConfiguration = boardConfiguration;

      this.boardConfiguration.columns.forEach((column, i) => {
        if (column.name !== 'Backlog') { return; }
        if (column.statuses.length === 0) { return; }

        const columnEntry = {};
        columnEntry['name'] = column.name;
        columnEntry['id'] = column.statuses[i].id;
        columnEntry['tasks'] = userStoriesData;
        this.backlogColumn = columnEntry;
      });
    });
  }

  private loadTeamData() {
    this.team = [];
    const projectName = this.settingsService.getProjectName();
    if (projectName) {
      this.personService.getMembers(projectName).subscribe(members => { this.team = members; });
    }
  }

  filter(field: string, term: string) {
    clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      if (typeof term === 'undefined' || term === null || term === '') {
        if (field === 'clearAll') {
          this.filterTerms = {};
          this.summaryFilter = '';
          this.selectedAssignee = '';
          this.selectedReporter = '';
          this.selectedIssuetype = '';
          this.selectedCreationDate = '';
        } else {
          delete this.filterTerms[field];
        }
      } else {
        this.filterTerms[field] = term.toLowerCase();
      }
      this.loadData();
    }, 600);
  }

  onDropBacklog(event: CdkDragDrop<Userstory[]>) {
    const fromContainer = event.previousContainer;
    const toContainer = event.container;
    const userStory: Userstory = fromContainer.data['tasks'][event.previousIndex];
    const backlogUserStories: Userstory[] = toContainer.data['tasks'];

    // Story dragged inside backlog container. Just change rank inside the container
    if (fromContainer === toContainer) {
      moveItemInArray(event.container.data['tasks'], event.previousIndex, event.currentIndex);
      this.changeRank(userStory, backlogUserStories);
      // Story dragged from board to backlog. Adjust rank and move subtasks with story
    } else if (userStory.isSubissue === false) {
      const newStatusId: number = +(event.container.data['id']);
      const newStatus = this.boardConfiguration.getStatus(newStatusId);

      userStory['tasks'].forEach(subtask => {
        subtask.status = newStatus;
        this.userStoryService.updateTask(this.settingsService.getProjectName(), subtask);
      });
      userStory.status = newStatus;

      this.parent.taskboardData = this.parent.taskboardData.filter(us => us.id !== userStory.id);
      this.tasks.splice(event.currentIndex, 0, userStory);

      this.userStoryService.updateIssue(this.settingsService.getProjectName(), userStory.id, userStory);
      this.changeRank(userStory, backlogUserStories);

      this.parent.prepareDataForBoard();
    }
  }

  /**
   * Changes the rank of user stories in backlog or sprint and updates in jira.
   * @param {Userstory} referenceStory - the value referenceStory
   * @param {Userstory} affectedUserStories - the value affectedUserStories
   **/
  private changeRank(referenceStory: Userstory, affectedUserStories: Userstory[]): void {
    const projectKey = this.settingsService.getProjectName();

    this.userStoryService.changeRank(projectKey, referenceStory, affectedUserStories).subscribe({
      complete() {
      }
    });
  }

  /**
   * Opens CreateUserstoryDialog and creates a new user story with name and description given from user-input after close
   */
  openCreateUserStoryDialog() {
    this.createUserStoryDialogRef = this.addUserStoryDialog.open(CreateUserstoryDialogComponent, {
      minWidth: '20vw',
      data: { name: null, description: null }
    });

    this.createUserStoryDialogRef.afterClosed().subscribe(userStoryData => {
      // NOTE: this is untested code, since we do not have a Kanban board available
      // check if user story is returned when closing dialog
      if (userStoryData) {
        this.userStoryService.createIssue(
          this.settingsService.getProjectName(),
          userStoryData.issueType,
          userStoryData.name,
          userStoryData.description,
          userStoryData.assignee,
          userStoryData.reporter,
          userStoryData.status.id,
          null,
          userStoryData.storyPoints
        ).subscribe(() => {
          // update Backlog UI
          this.loadData();
        });
      }
    });
  }

  /**
   * A minified issue-card has been double clicked. Open the detailed Issuecard (if it doesnt exist already) without
   * changing the position/view of the minfied card.
   */
  onIssueDoubleClicked(event: MouseEvent, issue: Issue) {
    /* Bubble event */
    this.parent.onIssueDoubleClicked(event, issue);
  }
}
